/* eslint-disable react-hooks/refs -- @dnd-kit node refs are used during render by design */
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { NavAppTile } from '@/platform/components/nav-app-tile';
import { navTileGridClass } from '@/platform/lib/nav-tile-layout';
import type { OverviewTile } from '@/platform/lib/overview-catalog';

function SortableOverviewTile({
  tile,
  editing,
  onOpen,
  onRemove,
}: {
  tile: OverviewTile;
  editing: boolean;
  onOpen: (path: string) => void;
  onRemove: (path: string) => void;
}) {
  const sortable = useSortable({ id: tile.path });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className="min-w-0"
      {...sortable.attributes}
      {...sortable.listeners}
    >
      <NavAppTile
        label={tile.label}
        path={tile.path}
        icon={tile.icon}
        onOpen={editing ? undefined : () => onOpen(tile.path)}
        badge={
          editing ? (
            <span className="absolute -top-1 -right-1 flex size-5 items-center justify-center rounded-full bg-foreground text-background">
              <button
                type="button"
                className="flex size-full items-center justify-center"
                aria-label={`${tile.label} entfernen`}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => onRemove(tile.path)}
              >
                <XIcon className="size-3" aria-hidden />
              </button>
            </span>
          ) : null
        }
      />
    </li>
  );
}

export function OverviewTileGrid({
  tiles,
  editing,
  onEditingChange,
  onOpen,
  onRemove,
  onReorder,
}: {
  tiles: readonly OverviewTile[];
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onOpen: (path: string) => void;
  onRemove: (path: string) => void;
  onReorder: (paths: string[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 500, tolerance: 8 } }),
  );

  function onDragStart(_event: DragStartEvent) {
    onEditingChange(true);
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const paths = tiles.map((tile) => tile.path);
    const from = paths.indexOf(String(active.id));
    const to = paths.indexOf(String(over.id));
    if (from < 0 || to < 0) {
      return;
    }
    onReorder(arrayMove(paths, from, to));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={tiles.map((tile) => tile.path)} strategy={rectSortingStrategy}>
        <ul className={cn(navTileGridClass, 'lg:justify-start')} aria-label="Persönliche Übersicht">
          {tiles.map((tile) => (
            <SortableOverviewTile
              key={tile.path}
              tile={tile}
              editing={editing}
              onOpen={onOpen}
              onRemove={onRemove}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
